import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Globe, CheckCircle, XCircle, Clock, Users, Briefcase, RefreshCw, ExternalLink, Loader2, Plus, Trash2, LogOut, AlertTriangle, Info, Database, Link as LinkIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API } from "@/App";
import Navbar from "@/components/Navbar";

const RESEARCH_FIELDS = [
  "Computer Science", "Biology", "Physics", "Chemistry", "Medicine",
  "Engineering", "Mathematics", "Environmental Science", "Neuroscience",
  "Economics", "Psychology", "Astronomy", "Materials Science", "Genetics",
  "Immunology", "Pharmacology", "Statistics", "Biochemistry", "Ecology"
];

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobSources, setJobSources] = useState({ builtin_sources: [], custom_sources: [], source_stats: {} });
  const [loading, setLoading] = useState(true);
  const [reviewingId, setReviewingId] = useState(null);
  const [adminNotes, setAdminNotes] = useState({});
  const [activeTab, setActiveTab] = useState("overview");
  const [showAddJob, setShowAddJob] = useState(false);
  const [showAddSource, setShowAddSource] = useState(false);
  const [sourceAnalysis, setSourceAnalysis] = useState(null);
  const [analyzingSource, setAnalyzingSource] = useState(false);
  const [bulkImportStatus, setBulkImportStatus] = useState({ running: false, progress: "" });
  const [dataQuality, setDataQuality] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [newJob, setNewJob] = useState({
    title: "", institution: "", department: "", location: "", country: "",
    field: "Computer Science", job_type: "postdoc", description: "",
    requirements: "", salary_range: "", deadline: "", source_url: ""
  });
  const [newSource, setNewSource] = useState({ name: "", url: "", description: "" });

  const [importingSource, setImportingSource] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Check admin auth
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const response = await fetch(`${API}/admin/check`, {
          credentials: "include",
          headers: getAuthHeaders(),
        });
        const data = await response.json();
        if (!data.is_admin) {
          navigate("/admin/login");
          return;
        }
        setIsAdmin(true);
      } catch (error) {
        navigate("/admin/login");
      }
    };
    checkAdmin();
  }, [navigate]);

  // Fetch admin stats
  useEffect(() => {
    if (!isAdmin) return;
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/admin/stats`, {
          credentials: "include",
          headers: getAuthHeaders(),
        });
        if (response.ok) setStats(await response.json());
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    fetchStats();
  }, [isAdmin]);

  // Fetch submissions
  useEffect(() => {
    if (!isAdmin) return;
    const fetchSubmissions = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API}/admin/websites/all`, {
          credentials: "include",
          headers: getAuthHeaders(),
        });
        if (response.ok) setSubmissions(await response.json());
      } catch (error) {
        console.error("Error fetching submissions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, [isAdmin]);

  // Fetch jobs for management
  useEffect(() => {
    if (!isAdmin || activeTab !== "jobs") return;
    const fetchJobs = async () => {
      try {
        const response = await fetch(`${API}/admin/jobs?limit=50`, {
          credentials: "include",
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          const data = await response.json();
          setJobs(data.jobs);
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
      }
    };
    fetchJobs();
  }, [isAdmin, activeTab]);

  // Fetch job sources
  useEffect(() => {
    if (!isAdmin || activeTab !== "sources") return;
    const fetchSources = async () => {
      try {
        const response = await fetch(`${API}/admin/job-sources`, {
          credentials: "include",
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          setJobSources(await response.json());
        }
      } catch (error) {
        console.error("Error fetching job sources:", error);
      }
    };
    fetchSources();
  }, [isAdmin, activeTab]);

  // Fetch data quality stats
  useEffect(() => {
    if (!isAdmin || activeTab !== "quality") return;
    const fetchDataQuality = async () => {
      try {
        const response = await fetch(`${API}/admin/data-quality`, {
          credentials: "include",
          headers: getAuthHeaders(),
        });
        if (response.ok) setDataQuality(await response.json());
      } catch (error) {
        console.error("Error fetching data quality:", error);
      }
    };
    fetchDataQuality();
  }, [isAdmin, activeTab]);

  const handleReview = async (submissionId, status) => {
    setReviewingId(submissionId);
    try {
      const response = await fetch(`${API}/admin/websites/${submissionId}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ status, admin_notes: adminNotes[submissionId] || null }),
      });
      if (response.ok) {
        toast.success(`Submission ${status}`);
        setSubmissions(prev => prev.map(s => 
          s.submission_id === submissionId ? { ...s, status, reviewed_at: new Date().toISOString() } : s
        ));
      } else {
        toast.error("Failed to review submission");
      }
    } catch (error) {
      toast.error("Error reviewing submission");
    } finally {
      setReviewingId(null);
    }
  };

  const handleAddJob = async () => {
    try {
      const jobData = { ...newJob, requirements: newJob.requirements.split("\n").filter(r => r.trim()) };
      const response = await fetch(`${API}/admin/jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify(jobData),
      });
      if (response.ok) {
        toast.success("Job created successfully!");
        setShowAddJob(false);
        setNewJob({
          title: "", institution: "", department: "", location: "", country: "",
          field: "Computer Science", job_type: "postdoc", description: "",
          requirements: "", salary_range: "", deadline: "", source_url: ""
        });
        const jobsRes = await fetch(`${API}/admin/jobs?limit=50`, { credentials: "include", headers: getAuthHeaders() });
        if (jobsRes.ok) setJobs((await jobsRes.json()).jobs);
      } else {
        toast.error("Failed to create job");
      }
    } catch (error) {
      toast.error("Error creating job");
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!confirm("Are you sure you want to delete this job?")) return;
    try {
      const response = await fetch(`${API}/admin/jobs/${jobId}`, {
        method: "DELETE",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        toast.success("Job deleted");
        setJobs(prev => prev.filter(j => j.job_id !== jobId));
      } else {
        toast.error("Failed to delete job");
      }
    } catch (error) {
      toast.error("Error deleting job");
    }
  };

  const handleAddSource = async () => {
    setAnalyzingSource(true);
    setSourceAnalysis(null);
    try {
      const response = await fetch(`${API}/admin/job-sources`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify(newSource),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success("Job source added!");
        setSourceAnalysis(data.analysis);
        // Refresh sources
        const sourcesRes = await fetch(`${API}/admin/job-sources`, { credentials: "include", headers: getAuthHeaders() });
        if (sourcesRes.ok) setJobSources(await sourcesRes.json());
      } else {
        toast.error(data.detail || "Failed to add source");
      }
    } catch (error) {
      toast.error("Error adding source");
    } finally {
      setAnalyzingSource(false);
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!confirm("Delete this job source?")) return;
    try {
      const response = await fetch(`${API}/admin/job-sources/${sourceId}`, {
        method: "DELETE",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        toast.success("Source deleted");
        setJobSources(prev => ({
          ...prev,
          custom_sources: prev.custom_sources.filter(s => s.source_id !== sourceId)
        }));
      }
    } catch (error) {
      toast.error("Error deleting source");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/admin/logout`, { method: "POST", credentials: "include", headers: getAuthHeaders() });
      localStorage.removeItem("admin_token");
      navigate("/admin/login");
    } catch (error) {
      localStorage.removeItem("admin_token");
      navigate("/admin/login");
    }
  };

  const handleBulkImport = async () => {
    try {
      setBulkImportStatus({ running: true, progress: "Starting bulk import from ALL sources..." });
      const response = await fetch(`${API}/admin/bulk-import`, {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success("Bulk import started! This imports from ALL configured sources.");
        pollImportStatus();
      } else {
        setBulkImportStatus({ running: false, progress: data.message || "Failed" });
        toast.error(data.message || "Failed to start import");
      }
    } catch (error) {
      setBulkImportStatus({ running: false, progress: "Error" });
      toast.error("Failed to start bulk import");
    }
  };

  const handleSourceImport = async (sourceName) => {
    try {
      setImportingSource(sourceName);
      setBulkImportStatus({ running: true, progress: `Importing from ${sourceName}...` });
      const response = await fetch(`${API}/admin/bulk-import/source/${encodeURIComponent(sourceName)}`, {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Import from ${sourceName} started!`);
        pollImportStatus();
      } else {
        setBulkImportStatus({ running: false, progress: data.message || "Failed" });
        setImportingSource(null);
        toast.error(data.message || "Failed to start import");
      }
    } catch (error) {
      setBulkImportStatus({ running: false, progress: "Error" });
      setImportingSource(null);
      toast.error(`Failed to import from ${sourceName}`);
    }
  };

  const handleDeepImport = async (sourceName) => {
    try {
      setImportingSource(`deep_${sourceName}`);
      setBulkImportStatus({ running: true, progress: `Deep importing from ${sourceName} (fetching detailed job info)...` });
      const response = await fetch(`${API}/admin/deep-import/${encodeURIComponent(sourceName)}`, {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Deep import from ${sourceName} started! This fetches individual job details for better quality.`);
        pollImportStatus();
      } else {
        setBulkImportStatus({ running: false, progress: data.message || "Failed" });
        setImportingSource(null);
        toast.error(data.message || "Failed to start deep import");
      }
    } catch (error) {
      setBulkImportStatus({ running: false, progress: "Error" });
      setImportingSource(null);
      toast.error(`Failed to deep import from ${sourceName}`);
    }
  };

  const handleEnrichJobs = async (sourceName = null) => {
    try {
      const sourceLabel = sourceName || "all sources";
      setImportingSource(`enrich_${sourceName || 'all'}`);
      setBulkImportStatus({ running: true, progress: `Enriching job data from ${sourceLabel}...` });
      
      const url = sourceName 
        ? `${API}/admin/enrich-jobs?batch_size=50&source=${encodeURIComponent(sourceName)}`
        : `${API}/admin/enrich-jobs?batch_size=100`;
      
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Job enrichment started! Fetching accurate deadlines and descriptions from ${sourceLabel}.`);
        pollImportStatus();
      } else {
        setBulkImportStatus({ running: false, progress: data.message || "Failed" });
        setImportingSource(null);
        toast.error(data.message || "Failed to start enrichment");
      }
    } catch (error) {
      setBulkImportStatus({ running: false, progress: "Error" });
      setImportingSource(null);
      toast.error("Failed to start job enrichment");
    }
  };

  const pollImportStatus = () => {
    const pollInterval = setInterval(async () => {
      try {
        const statusRes = await fetch(`${API}/admin/bulk-import/status`, { credentials: "include", headers: getAuthHeaders() });
        const statusData = await statusRes.json();
        setBulkImportStatus({ running: statusData.running, progress: statusData.progress });
        if (!statusData.running) {
          clearInterval(pollInterval);
          setImportingSource(null);
          const statsRes = await fetch(`${API}/admin/stats`, { credentials: "include", headers: getAuthHeaders() });
          if (statsRes.ok) setStats(await statsRes.json());
          // Refresh job sources to update counts
          const sourcesRes = await fetch(`${API}/admin/job-sources`, { credentials: "include", headers: getAuthHeaders() });
          if (sourcesRes.ok) setJobSources(await sourcesRes.json());
          if (statusData.last_result?.total_new > 0) {
            toast.success(`Import complete! Added ${statusData.last_result.total_new} new jobs.`);
          } else {
            toast.info("Import complete. No new jobs found.");
          }
        }
      } catch (e) { console.error("Error polling:", e); }
    }, 3000);
  };

  const handleUpdateJob = async (job) => {
    try {
      const response = await fetch(`${API}/admin/jobs/${job.job_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({
          title: job.title,
          description: job.description,
          deadline: job.deadline,
          institution: job.institution,
          location: job.location,
          salary_range: job.salary_range,
          requirements: job.requirements,
          needs_review: false
        }),
      });
      if (response.ok) {
        toast.success("Job updated successfully");
        setEditingJob(null);
        // Refresh jobs list
        const jobsRes = await fetch(`${API}/admin/jobs?limit=50`, { credentials: "include", headers: getAuthHeaders() });
        if (jobsRes.ok) {
          const data = await jobsRes.json();
          setJobs(data.jobs || []);
        }
      } else {
        toast.error("Failed to update job");
      }
    } catch (error) {
      toast.error("Error updating job");
    }
  };

  const handleFlagForReview = async (jobId) => {
    try {
      const response = await fetch(`${API}/admin/jobs/${jobId}/flag-review`, {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        toast.success("Job flagged for review");
      }
    } catch (error) {
      toast.error("Error flagging job");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString();
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#D4AF37]/10 rounded-sm">
              <Shield className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="font-serif text-3xl font-medium text-white">Admin Dashboard</h1>
              <p className="text-gray-400">Manage jobs, sources, and platform settings</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" className="border-white/20 text-white hover:bg-white/5">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-2xl font-bold text-white">{stats.jobs.total}</span>
                </div>
                <p className="text-gray-400 text-sm mt-1">Total Jobs</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-green-500" />
                  <span className="text-2xl font-bold text-white">{Object.keys(jobSources.source_stats || {}).length || stats.jobs.by_type?.postdoc || 0}</span>
                </div>
                <p className="text-gray-400 text-sm mt-1">Active Sources</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-purple-500" />
                  <span className="text-2xl font-bold text-white">{stats.websites?.pending || 0}</span>
                </div>
                <p className="text-gray-400 text-sm mt-1">Pending Reviews</p>
              </CardContent>
            </Card>
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="text-2xl font-bold text-white">{stats.users?.total || 0}</span>
                </div>
                <p className="text-gray-400 text-sm mt-1">Users</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Tabs */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-white/5 mb-6">
                <TabsTrigger value="overview" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Overview</TabsTrigger>
                <TabsTrigger value="quality" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Data Quality</TabsTrigger>
                <TabsTrigger value="sources" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Job Sources</TabsTrigger>
                <TabsTrigger value="jobs" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Manage Jobs</TabsTrigger>
                <TabsTrigger value="submissions" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Website Submissions</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview">
                <div className="space-y-6">
                  <div className="p-4 rounded-sm bg-white/5 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-2">Quick Actions</h3>
                    <div className="flex flex-wrap gap-3">
                      <Button onClick={() => setActiveTab("jobs")} className="bg-[#800020] hover:bg-[#600018]">
                        <Plus className="w-4 h-4 mr-2" /> Add New Job
                      </Button>
                      <Button onClick={() => setActiveTab("sources")} variant="outline" className="border-white/20 text-white hover:bg-white/5">
                        <Database className="w-4 h-4 mr-2" /> Manage Sources
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                        onClick={handleBulkImport}
                        disabled={bulkImportStatus.running}
                      >
                        {bulkImportStatus.running ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                        ) : (
                          <><RefreshCw className="w-4 h-4 mr-2" /> Bulk Import (ALL Sources)</>
                        )}
                      </Button>
                      <Button 
                        variant="outline" 
                        className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                        onClick={() => handleEnrichJobs()}
                        disabled={bulkImportStatus.running}
                        data-testid="enrich-all-jobs-btn"
                      >
                        {bulkImportStatus.running && importingSource?.startsWith('enrich_') ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enriching...</>
                        ) : (
                          <><CheckCircle className="w-4 h-4 mr-2" /> Enrich Job Data</>
                        )}
                      </Button>
                    </div>
                    {bulkImportStatus.running && bulkImportStatus.progress && (
                      <p className="mt-3 text-sm text-[#D4AF37]">{bulkImportStatus.progress}</p>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      Tip: "Enrich Job Data" fetches accurate deadlines and descriptions from source pages.
                    </p>
                  </div>
                  {stats?.last_refresh && (
                    <div className="p-4 rounded-sm bg-white/5 border border-white/10">
                      <p className="text-gray-400">Last job refresh: <span className="text-white">{formatDate(stats.last_refresh)}</span></p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Data Quality Tab */}
              <TabsContent value="quality">
                <div className="space-y-6">
                  <div className="p-4 rounded-sm bg-white/5 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-4">Data Quality Overview</h3>
                    {dataQuality ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-3 rounded bg-white/5">
                            <p className="text-2xl font-bold text-white">{dataQuality.total_jobs}</p>
                            <p className="text-xs text-gray-400">Total Jobs</p>
                          </div>
                          <div className="p-3 rounded bg-red-500/10 border border-red-500/20">
                            <p className="text-2xl font-bold text-red-400">{dataQuality.missing_deadline}</p>
                            <p className="text-xs text-gray-400">Missing Deadline</p>
                          </div>
                          <div className="p-3 rounded bg-orange-500/10 border border-orange-500/20">
                            <p className="text-2xl font-bold text-orange-400">{dataQuality.missing_description}</p>
                            <p className="text-xs text-gray-400">Short/Missing Desc</p>
                          </div>
                          <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/20">
                            <p className="text-2xl font-bold text-emerald-400">{dataQuality.enriched}</p>
                            <p className="text-xs text-gray-400">Enriched Jobs</p>
                          </div>
                        </div>
                        
                        <div className="p-3 rounded bg-white/5">
                          <p className="text-sm text-gray-400 mb-2">Overall Quality Score</p>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-3 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className={`h-full transition-all ${dataQuality.overall_quality_score > 70 ? 'bg-emerald-500' : dataQuality.overall_quality_score > 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${dataQuality.overall_quality_score}%` }}
                              />
                            </div>
                            <span className="text-white font-bold">{dataQuality.overall_quality_score}%</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400">Loading data quality stats...</p>
                    )}
                  </div>

                  <div className="p-4 rounded-sm bg-white/5 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-4">Quality by Source</h3>
                    {dataQuality?.by_source ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left py-2 px-3 text-gray-400">Source</th>
                              <th className="text-right py-2 px-3 text-gray-400">Jobs</th>
                              <th className="text-right py-2 px-3 text-gray-400">Missing Deadline</th>
                              <th className="text-right py-2 px-3 text-gray-400">Short Desc</th>
                              <th className="text-right py-2 px-3 text-gray-400">Quality</th>
                              <th className="text-right py-2 px-3 text-gray-400">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dataQuality.by_source.map((source, idx) => (
                              <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                                <td className="py-2 px-3 text-white">{source.source}</td>
                                <td className="py-2 px-3 text-right text-gray-300">{source.total}</td>
                                <td className="py-2 px-3 text-right">
                                  <span className={source.missing_deadline > 0 ? 'text-red-400' : 'text-green-400'}>
                                    {source.missing_deadline}
                                  </span>
                                </td>
                                <td className="py-2 px-3 text-right">
                                  <span className={source.short_description > 0 ? 'text-orange-400' : 'text-green-400'}>
                                    {source.short_description}
                                  </span>
                                </td>
                                <td className="py-2 px-3 text-right">
                                  <Badge className={source.quality_score > 70 ? 'bg-emerald-500/20 text-emerald-400' : source.quality_score > 40 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}>
                                    {source.quality_score}%
                                  </Badge>
                                </td>
                                <td className="py-2 px-3 text-right">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEnrichJobs(source.source)}
                                    disabled={bulkImportStatus.running}
                                    className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 text-xs"
                                  >
                                    Enrich
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-400">Loading source quality data...</p>
                    )}
                  </div>

                  <div className="p-4 rounded-sm bg-white/5 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-2">How to Fix Data Quality Issues</h3>
                    <ul className="text-sm text-gray-400 space-y-2">
                      <li>• <strong className="text-white">Missing Deadline</strong>: Click "Enrich" to fetch real deadlines from source pages</li>
                      <li>• <strong className="text-white">Short Description</strong>: The enricher will fetch full job descriptions automatically</li>
                      <li>• <strong className="text-white">Manual Edit</strong>: Go to "Manage Jobs" tab to edit individual listings</li>
                      <li>• <strong className="text-white">Bulk Enrich</strong>: Use the "Enrich Job Data" button in Overview for all jobs</li>
                    </ul>
                  </div>
                </div>
              </TabsContent>

              {/* Job Sources Tab */}
              <TabsContent value="sources">
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-white">Job Sources Configuration</h3>
                    <Dialog open={showAddSource} onOpenChange={setShowAddSource}>
                      <DialogTrigger asChild>
                        <Button className="bg-[#800020] hover:bg-[#600018]">
                          <Plus className="w-4 h-4 mr-2" /> Add Source
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Add New Job Source</DialogTitle>
                          <DialogDescription className="text-gray-400">
                            Enter the job source URL. The system will analyze it for scraping possibilities.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div>
                            <Label>Source Name *</Label>
                            <Input
                              value={newSource.name}
                              onChange={(e) => setNewSource({...newSource, name: e.target.value})}
                              className="bg-white/5 border-white/10"
                              placeholder="e.g., Nature Careers"
                            />
                          </div>
                          <div>
                            <Label>Source URL *</Label>
                            <Input
                              value={newSource.url}
                              onChange={(e) => setNewSource({...newSource, url: e.target.value})}
                              className="bg-white/5 border-white/10"
                              placeholder="https://careers.nature.com"
                            />
                          </div>
                          <div>
                            <Label>Description</Label>
                            <Textarea
                              value={newSource.description}
                              onChange={(e) => setNewSource({...newSource, description: e.target.value})}
                              className="bg-white/5 border-white/10"
                              placeholder="Brief description of this job source..."
                            />
                          </div>
                          {sourceAnalysis && (
                            <div className="p-4 rounded-sm border border-white/10 bg-white/5">
                              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                                <Info className="w-4 h-4" /> Source Analysis
                              </h4>
                              <div className="space-y-2 text-sm">
                                <p><span className="text-gray-400">Status:</span> <Badge className={sourceAnalysis.status === 'accessible' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>{sourceAnalysis.status}</Badge></p>
                                {sourceAnalysis.potential_listings > 0 && (
                                  <p className="text-green-400">Found {sourceAnalysis.potential_listings} potential job listings</p>
                                )}
                                {sourceAnalysis.possibilities?.length > 0 && (
                                  <div>
                                    <p className="text-green-400 font-semibold">Possibilities:</p>
                                    <ul className="list-disc list-inside text-gray-300">
                                      {sourceAnalysis.possibilities.map((p, i) => <li key={i}>{p}</li>)}
                                    </ul>
                                  </div>
                                )}
                                {sourceAnalysis.difficulties?.length > 0 && (
                                  <div>
                                    <p className="text-yellow-400 font-semibold">Difficulties:</p>
                                    <ul className="list-disc list-inside text-gray-300">
                                      {sourceAnalysis.difficulties.map((d, i) => <li key={i}>{d}</li>)}
                                    </ul>
                                  </div>
                                )}
                                {sourceAnalysis.recommendations?.length > 0 && (
                                  <div>
                                    <p className="text-blue-400 font-semibold">Recommendations:</p>
                                    <ul className="list-disc list-inside text-gray-300">
                                      {sourceAnalysis.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          <Button onClick={handleAddSource} disabled={analyzingSource || !newSource.name || !newSource.url} className="bg-[#D4AF37] text-black hover:bg-[#C5A028]">
                            {analyzingSource ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</> : "Analyze & Add Source"}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  {/* Built-in Sources */}
                  <div>
                    <h4 className="text-md font-semibold text-white mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" /> Global Sources
                    </h4>
                    <div className="grid gap-3">
                      {jobSources.builtin_sources?.filter(source => !source.category || source.category !== 'europe').map((source) => (
                        <div key={source.name} className="p-4 rounded-sm border border-white/10 bg-white/5">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="font-semibold text-white">{source.name}</h5>
                                <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                                <Badge className="bg-blue-500/20 text-blue-400">{source.job_count || 0} jobs</Badge>
                              </div>
                              <p className="text-gray-400 text-sm mb-2">{source.description}</p>
                              <div className="flex flex-wrap gap-2 text-xs">
                                <span className="text-gray-500">Regions: {source.regions?.join(", ")}</span>
                                <span className="text-gray-500">|</span>
                                <span className="text-gray-500">Est. jobs: {source.estimated_jobs}</span>
                              </div>
                              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                <div className="text-green-400"><span className="font-semibold">Possibilities:</span> {source.possibilities}</div>
                                <div className="text-yellow-400"><span className="font-semibold">Challenges:</span> {source.difficulties}</div>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2 ml-4">
                              <Button
                                size="sm"
                                onClick={() => handleSourceImport(source.name)}
                                disabled={bulkImportStatus.running}
                                className="bg-[#D4AF37] hover:bg-[#C5A028] text-black"
                              >
                                {importingSource === source.name ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Importing...</>
                                ) : (
                                  <><RefreshCw className="w-3 h-3 mr-1" /> Import</>
                                )}
                              </Button>
                              {/* Deep Import button - available for ALL sources */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeepImport(source.name)}
                                disabled={bulkImportStatus.running}
                                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 text-xs"
                                title="Fetches individual job pages for better descriptions and apply links (slower but higher quality)"
                              >
                                {importingSource === `deep_${source.name}` ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Deep...</>
                                ) : (
                                  <><Database className="w-3 h-3 mr-1" /> Deep Import</>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEnrichJobs(source.name)}
                                disabled={bulkImportStatus.running}
                                className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 text-xs"
                                title="Enrich existing jobs from this source with accurate deadlines and descriptions"
                              >
                                {importingSource === `enrich_${source.name}` ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Enriching...</>
                                ) : (
                                  <><CheckCircle className="w-3 h-3 mr-1" /> Enrich</>
                                )}
                              </Button>
                              <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline text-xs flex items-center gap-1 justify-center">
                                <ExternalLink className="w-3 h-3" /> Visit
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* European Sources Section */}
                  <div className="mt-6">
                    <h4 className="text-md font-semibold text-white mb-3 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-500" /> European Research Institutions
                      <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                        {jobSources.builtin_sources?.filter(source => source.category === 'europe').length || 0} sources
                      </Badge>
                    </h4>
                    <p className="text-gray-400 text-sm mb-4">
                      Premium job listings from top European universities and research organizations
                    </p>
                    <div className="grid gap-3">
                      {jobSources.builtin_sources?.filter(source => source.category === 'europe').map((source) => (
                        <div key={source.name} className="p-4 rounded-sm border border-blue-500/20 bg-blue-500/5">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="font-semibold text-white">{source.name}</h5>
                                <Badge className="bg-blue-500/20 text-blue-400">Europe</Badge>
                                <Badge className="bg-green-500/20 text-green-400">{source.job_count || 0} jobs</Badge>
                              </div>
                              <p className="text-gray-400 text-sm mb-2">{source.description}</p>
                              <div className="flex flex-wrap gap-2 text-xs">
                                <span className="text-gray-500">Regions: {source.regions?.join(", ")}</span>
                                <span className="text-gray-500">|</span>
                                <span className="text-gray-500">Est. jobs: {source.estimated_jobs}</span>
                              </div>
                              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                <div className="text-green-400"><span className="font-semibold">Possibilities:</span> {source.possibilities}</div>
                                <div className="text-yellow-400"><span className="font-semibold">Challenges:</span> {source.difficulties}</div>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2 ml-4">
                              <Button
                                size="sm"
                                onClick={() => handleSourceImport(source.name)}
                                disabled={bulkImportStatus.running}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                {importingSource === source.name ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Importing...</>
                                ) : (
                                  <><RefreshCw className="w-3 h-3 mr-1" /> Import</>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeepImport(source.name)}
                                disabled={bulkImportStatus.running}
                                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 text-xs"
                                title="Fetches individual job pages for better descriptions and apply links (slower but higher quality)"
                              >
                                {importingSource === `deep_${source.name}` ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Deep...</>
                                ) : (
                                  <><Database className="w-3 h-3 mr-1" /> Deep Import</>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEnrichJobs(source.name)}
                                disabled={bulkImportStatus.running}
                                className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 text-xs"
                                title="Enrich existing jobs from this source with accurate deadlines and descriptions"
                              >
                                {importingSource === `enrich_${source.name}` ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Enriching...</>
                                ) : (
                                  <><CheckCircle className="w-3 h-3 mr-1" /> Enrich</>
                                )}
                              </Button>
                              <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline text-xs flex items-center gap-1 justify-center">
                                <ExternalLink className="w-3 h-3" /> Visit
                              </a>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Custom Sources */}
                  {jobSources.custom_sources?.length > 0 && (
                    <div>
                      <h4 className="text-md font-semibold text-white mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-yellow-500" /> Custom Sources (Pending Implementation)
                      </h4>
                      <div className="grid gap-3">
                        {jobSources.custom_sources.map((source) => (
                          <div key={source.source_id} className="p-4 rounded-sm border border-white/10 bg-white/5">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <h5 className="font-semibold text-white">{source.name}</h5>
                                  <Badge className={source.enabled ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                                    {source.enabled ? "Enabled" : "Pending"}
                                  </Badge>
                                </div>
                                <p className="text-gray-400 text-sm">{source.description || source.url}</p>
                                {source.analysis && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Analysis: {source.analysis.status} | {source.analysis.potential_listings || 0} potential listings
                                  </p>
                                )}
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteSource(source.source_id)} className="text-red-400 hover:bg-red-500/10">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Jobs Management Tab */}
              <TabsContent value="jobs">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-white">Job Listings</h3>
                    <Dialog open={showAddJob} onOpenChange={setShowAddJob}>
                      <DialogTrigger asChild>
                        <Button className="bg-[#800020] hover:bg-[#600018]"><Plus className="w-4 h-4 mr-2" /> Add Job</Button>
                      </DialogTrigger>
                      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Add New Job Listing</DialogTitle>
                          <DialogDescription className="text-gray-400">Fill in the details to create a new job posting</DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div><Label>Title *</Label><Input value={newJob.title} onChange={(e) => setNewJob({...newJob, title: e.target.value})} className="bg-white/5 border-white/10" placeholder="Postdoctoral Researcher..." /></div>
                            <div><Label>Institution *</Label><Input value={newJob.institution} onChange={(e) => setNewJob({...newJob, institution: e.target.value})} className="bg-white/5 border-white/10" placeholder="University name" /></div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div><Label>Department</Label><Input value={newJob.department} onChange={(e) => setNewJob({...newJob, department: e.target.value})} className="bg-white/5 border-white/10" placeholder="Department of..." /></div>
                            <div>
                              <Label>Research Field *</Label>
                              <Select value={newJob.field} onValueChange={(v) => setNewJob({...newJob, field: v})}>
                                <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-[#0A0A0A] border-white/10">
                                  {RESEARCH_FIELDS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div><Label>Location *</Label><Input value={newJob.location} onChange={(e) => setNewJob({...newJob, location: e.target.value})} className="bg-white/5 border-white/10" placeholder="City" /></div>
                            <div><Label>Country *</Label><Input value={newJob.country} onChange={(e) => setNewJob({...newJob, country: e.target.value})} className="bg-white/5 border-white/10" placeholder="Country" /></div>
                            <div>
                              <Label>Type *</Label>
                              <Select value={newJob.job_type} onValueChange={(v) => setNewJob({...newJob, job_type: v})}>
                                <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-[#0A0A0A] border-white/10">
                                  <SelectItem value="postdoc">Postdoc</SelectItem>
                                  <SelectItem value="job">Job</SelectItem>
                                  <SelectItem value="fellowship">Fellowship</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div><Label>Description *</Label><Textarea value={newJob.description} onChange={(e) => setNewJob({...newJob, description: e.target.value})} className="bg-white/5 border-white/10 min-h-[100px]" placeholder="Position description..." /></div>
                          <div><Label>Requirements (one per line)</Label><Textarea value={newJob.requirements} onChange={(e) => setNewJob({...newJob, requirements: e.target.value})} className="bg-white/5 border-white/10" placeholder="PhD required&#10;Strong programming skills" /></div>
                          <div className="grid grid-cols-2 gap-4">
                            <div><Label>Deadline</Label><Input type="date" value={newJob.deadline} onChange={(e) => setNewJob({...newJob, deadline: e.target.value})} className="bg-white/5 border-white/10" /></div>
                            <div><Label>Application URL</Label><Input value={newJob.source_url} onChange={(e) => setNewJob({...newJob, source_url: e.target.value})} className="bg-white/5 border-white/10" placeholder="https://..." /></div>
                          </div>
                          <Button onClick={handleAddJob} className="bg-[#D4AF37] text-black hover:bg-[#C5A028]">Create Job Listing</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {jobs.map((job) => (
                      <div key={job.job_id} className="p-4 rounded-sm border border-white/10 bg-white/5 flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={job.job_type === 'postdoc' ? 'bg-blue-500/20 text-blue-400' : job.job_type === 'fellowship' ? 'bg-purple-500/20 text-purple-400' : 'bg-green-500/20 text-green-400'}>
                              {job.job_type}
                            </Badge>
                            <span className="text-xs text-gray-500">{job.source}</span>
                          </div>
                          <h4 className="font-semibold text-white">{job.title}</h4>
                          <p className="text-gray-400 text-sm">{job.institution} - {job.country}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteJob(job.job_id)} className="text-red-400 hover:bg-red-500/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Submissions Tab */}
              <TabsContent value="submissions">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">User-Submitted Websites</h3>
                  {loading ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>
                  ) : submissions.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">No website submissions yet.</p>
                  ) : (
                    <div className="space-y-4">
                      {submissions.map((sub) => (
                        <div key={sub.submission_id} className="p-4 rounded-sm border border-white/10 bg-white/5">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold text-white">{sub.name}</h4>
                                <Badge className={sub.status === 'approved' ? 'bg-green-500/20 text-green-400' : sub.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}>
                                  {sub.status}
                                </Badge>
                              </div>
                              <a href={sub.url} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline text-sm flex items-center gap-1">
                                <Globe className="w-3 h-3" /> {sub.url}
                              </a>
                              {sub.description && <p className="text-gray-400 text-sm mt-2">{sub.description}</p>}
                            </div>
                            {sub.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => handleReview(sub.submission_id, 'approved')} disabled={reviewingId === sub.submission_id} className="bg-green-600 hover:bg-green-700">
                                  {reviewingId === sub.submission_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleReview(sub.submission_id, 'rejected')} disabled={reviewingId === sub.submission_id} className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
